import React, { useState, useEffect } from "react"
import { values } from "lodash"
import Button from "@convertkit/design/button"
import BillingAddressInput from "app/shared/components/billing-address-input"
import useCurrentUser from "hooks/use-current-user"
import makeRequest from "../../account/utils/make-request"

export default function BillingAddressForm(props) {
  const user = useCurrentUser()

  const [billingAddress, setBillingAddress] = useState(props.address || "")
  const [billingCity, setBillingCity] = useState(props.city || "")
  const [billingState, setBillingState] = useState(props.state || "")
  const [billingPostalCode, setBillingPostalCode] = useState(
    props.postalCode || ""
  )
  const [billingCountry, setBillingCountry] = useState(
    props.country || user.ip_country || ""
  )

  const [busy, setBusy] = useState(false)
  const [submittable, setSubmittable] = useState(false)
  const [error, setError] = useState(null)
  const [showUpdateCardMessage, setShowUpdateCardMessage] = useState(false)
  const [validity, setValidity] = useState({
    address: false,
    country: false,
    city: false,
    state: false,
    postalCode: false
  })

  useEffect(() => {
    validity.address = isValidString(billingAddress)
    validity.city = isValidString(billingCity)
    validity.state = isValidString(billingState)
    validity.postalCode = isValidString(billingPostalCode, 10)
    validity.country = isValidString(billingCountry)

    setValidity(validity)
    setSubmittable(!values(validity).includes(false))
  }, [])

  const submitButtonText = busy ? "Working..." : "Update Address"

  const handleAddressChange = value => {
    setBillingAddress(value)

    const valid = isValidString(value)
    validity.address = valid
    setValidity(validity)

    updateError(valid ? null : "Street Address is required")
  }

  const handleCityChange = value => {
    setBillingCity(value)

    const valid = isValidString(value)
    validity.city = valid
    setValidity(validity)

    updateError(valid ? null : "City is required")
  }

  const handleStateChange = value => {
    setBillingState(value)

    const valid = isValidString(value)
    validity.state = valid
    setValidity(validity)

    updateError(valid ? null : "State is required")
  }

  const handlePostalCodeChange = value => {
    setBillingPostalCode(value)

    const valid = isValidString(value, 10)
    validity.postalCode = valid
    setValidity(validity)

    updateError(valid ? null : "Postal code is invalid")
  }

  const handleCountryChange = value => {
    setBillingCountry(value)

    const valid = isValidString(value)
    validity.country = valid
    setValidity(validity)

    updateError(valid ? null : "Country is required")
  }

  const isValidString = (value, max = 150) => {
    // Checking max length is just a precaution against maliciously large values being submitted in the form
    if (!value) {
      return false
    }

    const string = `${value}`
    return !!string && string.length > 0 && string.length <= max
  }

  const updateError = errorMessage => {
    setError(errorMessage)
    setShowUpdateCardMessage(false)
    setSubmittable(!errorMessage && !values(validity).includes(false))
  }

  const handleSubmit = async event => {
    event.preventDefault()

    setError(null)
    setShowUpdateCardMessage(false)
    setBusy(true)

    const response = await makeRequest({
      url: "/billing/addresses/update_address",
      method: "PUT",
      body: {
        address: {
          address: billingAddress,
          city: billingCity,
          state: billingState,
          postal_code: billingPostalCode,
          country: billingCountry
        }
      }
    })

    if (response.error) {
      setError(response.error)
      setShowUpdateCardMessage(true)
      setBusy(false)
    } else {
      props.onSubmitted()
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-sm-12">
          <BillingAddressInput
            address={billingAddress}
            city={billingCity}
            state={billingState}
            postalCode={billingPostalCode}
            country={billingCountry}
            onAddressChange={handleAddressChange}
            onCityChange={handleCityChange}
            onStateChange={handleStateChange}
            onPostalCodeChange={handlePostalCodeChange}
            onCountryChange={handleCountryChange}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12 flex justify-end mt-8">
          <Button
            color="green"
            className="font-semibold w-40"
            disabled={busy || !submittable}
          >
            {submitButtonText}
          </Button>
        </div>
      </div>

      {error && (
        <div className="row mt-4">
          <div className="col-sm-12">
            <div className="errors">
              {error}
              {showUpdateCardMessage && (
                <p>
                  Try updating your credit card in{" "}
                  <a
                    href="/account_settings/billing"
                    className="font-semibold hover:text-gray-700"
                  >
                    billing settings.
                  </a>
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </form>
  )
}
